import { withWorkItems, WORKITEMS_STORE } from '@dxp/core/stores';
import {
    signalStore,
    withComputed,
    withFeature,
    withLinkedState,
    withMethods,
    withState,
} from '@ngrx/signals';
import { inject, linkedSignal } from '@angular/core';
import {
    IVoiceAction,
    IVoiceActions,
    IVoiceTask,
    IVoiceTasks,
    VoiceTask,
} from '@dxp/core/models';
import { differenceWith, isEqual } from 'lodash';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { distinctUntilChanged, EMPTY, filter, pipe, switchMap } from 'rxjs';
import { PermissionService, SipService } from '@dxp/core/services';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';

export const VoiceStore = signalStore(
    { providedIn: 'root' },
    withState<IVoiceTasks>({
        voiceTasks: {},
        previousActions: {},
    }),
    withDevtools('voiceStore'),
    withFeature(() => withWorkItems(inject(WORKITEMS_STORE))),
    withComputed((store, permissionService = inject(PermissionService)) => ({
        outboundCallState: () => {
            const hasOutboundCall = store
                .workItems()
                .find(workItem => workItem.isOutbound);
            if (!hasOutboundCall) {
                return null;
            }
            return hasOutboundCall?.conversations[0]?.outboundCallState;
        },
        hasVoiceTask: () => {
            /**
             * @todo get the workItem details used for the call - merge with the active voice task - to read directly from the store
             */
            const workItemId = store.activeWorkItem()?.workItemId;
            return workItemId ? store.voiceTasks()[workItemId] : null;
        },
        /**
         * @question Do we need to add in a check for the ActiveVoiceTask actions "onHold" property - To prevent the hold button delay
         */
        activeVoiceTaskOnHold: () => {
            /**
             * Get the active work item - Set by the active work Item Id
             */
            const activeWorkItem = store.activeWorkItem();

            /**
             * Get the active conversation set by the active workItem
             */
            const activeConversation = store.activeConversation();

            /**
             * Check to see if the workItem EnquiryCall State is set
             * If the user' call should be on hold
             */
            const onHold = [
                'OnHold',
                'Disconnected',
                'Trying',
                'Connected',
                'AllOnHold',
            ].some(
                allowedState =>
                    allowedState ===
                    activeWorkItem?.conversations[0]?.enquiryCallState,
            );

            /**
             * Check to see if hold should be disabled
             */
            const disabled =
                activeConversation?.enquiryCallState === 'Trying' ||
                activeConversation?.enquiryCallState === 'Connected' ||
                activeConversation?.isInternalCall ||
                (activeWorkItem?.isEnquiryCall ?? false);

            return {
                onHold,
                disabled,
            };
        },
        paymentsEnabled: () =>
            permissionService.hasAllPermissions(['voicepayment.read']),
    })),
    withLinkedState(store => ({
        activeVoiceTask: linkedSignal({
            source: store.voiceTasks,
            computation: () => store.hasVoiceTask(),
        }),
        voiceAction: linkedSignal({
            source: store.voiceTasks,
            computation: newVoiceTasks => {
                const workItemId: string | null =
                    store.activeWorkItem()?.workItemId ?? null;

                /**
                 * Only process this logic - If a workItem is available
                 */
                if (workItemId) {
                    const newActiveVoiceTask: IVoiceTask =
                        newVoiceTasks[workItemId] ?? null;
                    const previousVoiceTaskActions =
                        store.previousActions()[workItemId] ?? null;

                    /**
                     * Validate that both the Current & Previous Active WorkItems Exist
                     */
                    if (newActiveVoiceTask && previousVoiceTaskActions) {
                        const mappedPreviousActions = Object.keys(
                            previousVoiceTaskActions,
                        ).map((key: string) => ({
                            actionName: key,
                            value: previousVoiceTaskActions[
                                key as keyof typeof previousVoiceTaskActions
                            ],
                        }));
                        const mappedNewActions = Object.keys(
                            newActiveVoiceTask.actions,
                        ).map((key: string) => ({
                            actionName: key,
                            value: newActiveVoiceTask.actions[
                                key as keyof typeof newActiveVoiceTask.actions
                            ],
                        }));
                        const latestAction = differenceWith(
                            mappedNewActions,
                            mappedPreviousActions,
                            isEqual,
                        );

                        return latestAction[0];
                    }
                }

                return null;
            },
        }),
    })),
    withMethods((store, sipService = inject(SipService)) => ({
        _updateVoiceAction(action: keyof IVoiceActions): void {
            const workItemId = store.activeWorkItem()?.workItemId;
            const activeVoiceTask = store.activeVoiceTask();

            if (workItemId && activeVoiceTask) {
                /**
                 * Create a new object to store the previous actions - To later be compared
                 */
                const previousActiveActions = {
                    ...{},
                    ...activeVoiceTask.actions,
                };

                /**
                 * Toggle the action state
                 */
                activeVoiceTask.actions[action] =
                    !activeVoiceTask.actions[action];

                /**
                 * Update the Voice Task with the correct toggled value
                 */
                updateState(store, 'UPDATE_VOICE_ACTION', state => ({
                    ...state,
                    previousActions: {
                        ...state.previousActions,
                        ...{
                            [workItemId]: previousActiveActions,
                        },
                    },
                    voiceTasks: {
                        ...state.voiceTasks,
                        ...{
                            [workItemId]: activeVoiceTask,
                        },
                    },
                }));
            }
        },
        processActions: rxMethod<IVoiceAction | never>(
            pipe(
                distinctUntilChanged(),
                filter((action: IVoiceAction) => {
                    return !(action === null || action === undefined);
                }),
                switchMap((action: IVoiceAction) => {
                    /**
                     * @event Muted - Mute the call
                     */
                    if (action.actionName === 'muted') {
                        /**
                         * Invert the value
                         *
                         * Muted true - Should Mute [False]
                         * Muted False - Should UnMute [True]
                         */
                        sipService.toggleMute(!action.value);
                        return EMPTY;
                    }

                    return EMPTY;
                }),
            ),
        ),
        createVoiceTask(id: string): void {
            /**
             * Check to see if a voice task exists
             */
            const hasVoiceTask = store.voiceTasks()[id] ?? null;

            /**
             *
             */
            if (!hasVoiceTask) {
                /**
                 * Create an instance of the voice task VoiceTask
                 */
                const voiceTask = new VoiceTask().get();

                /**
                 * Create the initial state object mapped to the WorkItemId
                 */
                updateState(store, 'CREATE_VOICE_TASK', state => ({
                    ...state,
                    previousActions: {
                        ...state.previousActions,
                        ...{
                            [id]: voiceTask.actions,
                        },
                    },
                    voiceTasks: {
                        ...state.voiceTasks,
                        ...{
                            [id]: voiceTask,
                        },
                    },
                }));
            }
        },
        toggleMuteButton(): void {
            this._updateVoiceAction('muted');
        },
        toggleDTMFKeypad(): void {
            this._updateVoiceAction('enableDTMFKeypad');
        },
        togglePayments(): void {
            this._updateVoiceAction('enablePayments');
        },
    })),
);
